/**
 * 应用入口：把内核、渲染、输入、HUD、弹窗接起来。
 *
 * 唯一入口是 /play/，题目只能来自服务端当日题单。
 *
 * 没有「从 URL 里读题」这条路：sbn 是题面本身，一旦能从地址栏塞进来，
 * 就等于把题库变成了可传播的链接。分享给出的一律是玩法页网址。
 */

import { GameSession } from '../core/session.js';
import { BoardView } from '../view/boardView.js';
import { BoardInput } from '../view/input.js';
import { applyTheme, systemMode } from '../view/theme.js';
import { regionColor } from '../view/palette.js';

import { buildLevel } from './levels.js';
import { Hud } from './hud.js';
import { Sfx } from './audio.js';
import { initI18n, applyDom, setLang, lang, LANG_NAMES, t } from './i18n.js';
import {
    loadProgress, markSolved, loadSettings, saveSettings, welcomeSeen, markWelcomeSeen,
} from './storage.js';
import { todayKey, fetchDaily, fetchPuzzle, unsealNext, formatDate, PUZZLES_PER_DAY } from './daily.js';
import { loadLead, submitLead } from './lead.js';
import { sendStart, AttemptTracker } from './telemetry.js';
import {
    initPopups, closePopup, showSuccess, showFailure, showShare, showError,
    showDailyDone, showLanguage, showWelcome,
} from './popups.js';

const AUDIO_URL = 'audio';

/** 今天的题号（服务端下发，升序）。只有题号，题面开一道取一道。 */
/** @type {number[]} */
let slots = [];
/** 题单日期（服务器时区）——遥测的 date 一律用它。 */
let planDate = '';
/** 当前题在今天题组里的序号（0..9）。 */
let slot = 0;
/** 本地日期键（仅用于进度存储）。 */
let today = todayKey();
/** 当前尝试的遥测计量器。 */
/** @type {import('./telemetry.js').AttemptTracker | null} */
let tracker = null;
/** @type {import('./levels.js').Level | null} */
let level = null;
/** @type {GameSession | null} */
let session = null;

let view, input, hud, sfx, settings;
/** @type {'light'|'dark'} */
let themeMode = 'light';

const $ = sel => document.querySelector(sel);

// ── 启动 ──

async function boot() {
    settings = loadSettings();
    themeMode = settings.theme || systemMode();
    applyTheme(themeMode);

    await initI18n();

    sfx = new Sfx(AUDIO_URL, settings.sound);

    view = new BoardView($('#board'));
    input = new BoardInput(view);
    hud = new Hud({
        hearts: $('#hearts'),
        progressLabel: $('#progress-label'),
    });
    initPopups($('#popup-host'));

    wireChrome();

    // 首个手势解锁音频（iOS 要求）
    const unlock = () => sfx.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });

    $('#date-text').textContent = formatDate(document.documentElement.lang || 'en');

    try {
        const plan = await fetchDaily(loadProgress(today).solved);
        if (!plan.ready) { showPreparing(); return; }
        slots = plan.slots;
        planDate = plan.date;

        const next = firstUnsolvedSlot();
        if (next === -1) { showAllDone(); return; }
        // /api/daily 通常已经顺带把这一道题封装好带回来了，省一次 /api/puzzle 往返；
        // 没带到（比如本地进度跟服务端算出来的不是同一道）就照旧现取
        await startSlot(next, await unsealNext(plan));

        // 引导放在开局之后：关掉弹窗底下就是画好的棋盘，
        // 而不是让玩家对着空屏读完说明再等加载
        // 不动 input.enabled：遮罩是全屏且唯一的堆叠层，指针事件根本到不了棋盘；
        // 而 startLevel 的入场动画回调随后还会把它设回 true，写了也是白写
        if (next === 0 && !welcomeSeen(today)) {
            markWelcomeSeen(today);
            showWelcome({ total: slots.length, onStart: closePopup });
        }
    } catch (e) {
        showError(
            e instanceof Error ? e.message : String(e),
            () => { closePopup(); location.href = '/'; },
        );
    }
}

const ICON_DAY = '☀️';
const ICON_NIGHT = '🌙';
const ICON_SOUND_ON = '🔊';
const ICON_SOUND_OFF = '🔇';

// ── 提示（教学式：先看后应用，docs/hint-feature-spec.md §3） ──

/** 展示中的提示；null = 未展示。 */
/** @type {{hint: import('../core/hintEngine.js').Hint, timer: number} | null} */
let activeHint = null;
/** 正在代做提示结论（此间的 cellChanged 不应关闭提示 UI）。 */
let applyingHint = false;

/** 「应用」按钮浮现前的反应时间。 */
const HINT_APPLY_DELAY_MS = 1000;
/** 多格结论逐格落下的间隔。 */
const HINT_STEP_MS = 150;

/** i18n 缺词时的英文兜底（与 en.json 的 play.hint 保持同文）。 */
const HINT_FALLBACK = {
    apply: 'Apply',
    wrong_mark: 'The X at row {row}, column {col} is wrong — remove it first, then keep going.',
    adjacency: 'Stars never touch: the cells around this star can all be crossed out.',
    adjacency_full: "This star's row, column and surrounding cells can't hold another star — cross them all out.",
    row_complete: 'Row {line} already has all its stars — the rest of the row can be crossed out.',
    col_complete: 'Column {line} already has all its stars — the rest of the column can be crossed out.',
    region_complete: 'The {region} color already has all its stars — the rest of it can be crossed out.',
    last_cell_row: 'Row {line} has only one cell left for a star!',
    last_cell_col: 'Column {line} has only one cell left for a star!',
    last_cell_region: 'The {region} color has only one cell left for a star!',
    region_confined_row: 'The empty cells left for the {region} color (gold rings) are all in row {line} — cells of row {line} outside that color (faint X) can\'t hold a star and can be crossed out.',
    region_confined_col: 'The empty cells left for the {region} color (gold rings) are all in column {line} — cells of column {line} outside that color (faint X) can\'t hold a star and can be crossed out.',
    exclusion: 'If the faint-X cell held a star, it would block every spot left for the {region} color (gold rings) — leaving it nowhere for its star, so this cell can only be an X.',
    undercounting_row: 'The empty cells left for these {n} colors ({regions}, gold rings) all sit in rows {lines}, and their {n} stars fill those rows exactly — other colors\' cells there (faint X) can be crossed out.',
    undercounting_col: 'The empty cells left for these {n} colors ({regions}, gold rings) all sit in columns {lines}, and their {n} stars fill those columns exactly — other colors\' cells there (faint X) can be crossed out.',
    overcounting_row: 'The empty cells left in rows {lines} (gold rings) all belong to these {n} colors ({regions}), which those rows\' stars use up — those colors\' cells outside rows {lines} (faint X) can be crossed out.',
    overcounting_col: 'The empty cells left in columns {lines} (gold rings) all belong to these {n} colors ({regions}), which those columns\' stars use up — those colors\' cells outside columns {lines} (faint X) can be crossed out.',
    finned_counts_row: 'The empty cells left for these {n} colors ({regions}, gold rings) are almost all in rows {lines}; the one exception is the red-ringed cell. If it holds a star, every faint-X cell touches it; if it doesn\'t, the {n} colors fill rows {lines} — either way, the faint-X cells can be crossed out.',
    finned_counts_col: 'The empty cells left for these {n} colors ({regions}, gold rings) are almost all in columns {lines}; the one exception is the red-ringed cell. If it holds a star, every faint-X cell touches it; if it doesn\'t, the {n} colors fill columns {lines} — either way, the faint-X cells can be crossed out.',
    fish_col: 'The empty cells left in columns {setLines} (gold rings) all sit in rows {confineLines}, filling them exactly — other columns\' cells in rows {confineLines} (faint X) can be crossed out.',
    fish_row: 'The empty cells left in rows {setLines} (gold rings) all sit in columns {confineLines}, filling them exactly — other rows\' cells in columns {confineLines} (faint X) can be crossed out.',
    finned_fish_col: 'The empty cells left in columns {setLines} (gold rings) are almost all in rows {baseLines}; the one exception is the red-ringed cell. If it holds a star, every faint-X cell touches it; if it doesn\'t, those columns fill rows {baseLines} — either way, the faint-X cells can be crossed out.',
    finned_fish_row: 'The empty cells left in rows {setLines} (gold rings) are almost all in columns {baseLines}; the one exception is the red-ringed cell. If it holds a star, every faint-X cell touches it; if it doesn\'t, those rows fill columns {baseLines} — either way, the faint-X cells can be crossed out.',
    fish_sashimi_col: 'The empty cells left in columns {setLines} (gold rings) are almost all in rows {baseLines}; the one exception is the red-ringed cell. If it holds a star, every faint-X cell touches it; if it doesn\'t, those columns fill rows {baseLines} — either way, the faint-X cells can be crossed out.',
    fish_sashimi_row: 'The empty cells left in rows {setLines} (gold rings) are almost all in columns {baseLines}; the one exception is the red-ringed cell. If it holds a star, every faint-X cell touches it; if it doesn\'t, those rows fill columns {baseLines} — either way, the faint-X cells can be crossed out.',
    reveal: "This step is tricky — here's a star to help you along.",
};

/** 列表型参数的连接符：中文/日文顿号，其余逗号。 */
function listSep() {
    const L = lang();
    return (L.startsWith('zh') || L === 'ja') ? '、' : ', ';
}

/** 区域色块：文案里 {region}/{regions} 的可视指代，颜色与棋盘上该区域一致。 */
function regionChip(regionId) {
    const chip = document.createElement('span');
    chip.className = 'sd-region-chip';
    chip.style.background = regionColor(regionId);
    return chip;
}

/** 列的显示名：字母（1→A）。行用数字、列用字母，两轴记号不同不会混淆。 */
const colName = n => String.fromCharCode(64 + n);

/**
 * 把 meta 里的行列号换算成显示值：行保持数字，列换成字母。
 * 各 token 属于哪个轴见 hintEngine.js 头注释（line/lines/setLines 在 axis 轴，
 * confineLines/baseLines 在另一轴，col 恒为列）。
 */
function hintParams(meta = {}) {
    const { axis } = meta;
    const other = axis === 'row' ? 'col' : 'row';
    const fmt = (v, ax) => {
        const one = x => (ax === 'col' ? colName(x) : String(x));
        return Array.isArray(v) ? v.map(one).join(listSep()) : one(v);
    };
    const p = { ...meta, n: meta.k ?? meta.n };
    if (meta.col != null) p.col = colName(meta.col);
    if (axis) {
        if (meta.line != null) p.line = fmt(meta.line, axis);
        if (meta.lines) p.lines = fmt(meta.lines, axis);
        if (meta.setLines) p.setLines = fmt(meta.setLines, axis);
        if (meta.confineLines) p.confineLines = fmt(meta.confineLines, other);
        if (meta.baseLines) p.baseLines = fmt(meta.baseLines, other);
    }
    return p;
}

/**
 * 把带 {token} 的提示模板渲染成 DOM。
 * 数字与行列号列表填成文字（文案↔棋盘徽标同源），区域 id 换成同色色块。
 * 模板里出现文案没准备参数的 token 时原样保留 —— 便于一眼看出漏配。
 */
function renderHintText(el, template, meta = {}) {
    const params = hintParams(meta);
    el.replaceChildren();
    for (const part of template.split(/(\{\w+\})/)) {
        if (!part) continue;
        const m = /^\{(\w+)\}$/.exec(part);
        const v = m ? params[m[1]] : undefined;
        if (!m || v == null) {
            el.appendChild(document.createTextNode(part));
        } else if (m[1] === 'region') {
            el.appendChild(regionChip(v));
        } else if (m[1] === 'regions') {
            for (const rid of v) el.appendChild(regionChip(rid));
        } else {
            el.appendChild(document.createTextNode(String(v)));
        }
    }
}

/**
 * 从提示的 meta 里摘出文案点过名的行/列号（1-based），交给棋盘点亮徽标。
 * meta 的约定见 hintEngine.js 头注释：带 axis 的线号在 axis 轴上，
 * confineLines/baseLines 在另一轴上，row/col 是单格坐标。
 */
function hintLineRefs(hint) {
    const m = hint.meta ?? {};
    const rows = new Set(), cols = new Set();
    const add = (axis, nums) => { for (const L of nums ?? []) (axis === 'row' ? rows : cols).add(L); };
    if (m.row) rows.add(m.row);
    if (m.col) cols.add(m.col);
    if (m.axis) {
        add(m.axis, m.line != null ? [m.line] : undefined);
        add(m.axis, m.lines);
        add(m.axis, m.setLines);
        const other = m.axis === 'row' ? 'col' : 'row';
        add(other, m.confineLines);
        add(other, m.baseLines);
    }
    return { rows: [...rows], cols: [...cols] };
}

/** 收起提示（气泡、高亮、应用按钮计时器）。 */
function dismissHint() {
    if (!activeHint) return;
    clearTimeout(activeHint.timer);
    activeHint = null;
    $('#hint-bubble').hidden = true;
    $('#hint-apply').hidden = true;
    view.clearHighlight();
    view.clearPreview();
    view.clearFins();
    view.clearLineBadges();
}

/** 点击提示按钮：展示"下一步"，不落子。 */
function onHintClick() {
    if (!session || session.isOver || activeHint) return;
    const hint = session.nextHint();
    if (!hint) return;

    tracker?.hint();
    // 视觉分工：金圈只圈"推理依据"，结论格只留虚叉（幽灵标记），鳍格只留红圈——
    // 一格一种记号，文案里的"金圈/红圈/虚叉"才指得准
    const taken = new Set([...hint.actions, ...(hint.fins ?? [])].map(p => p.r * 100 + p.c));
    view.highlight(hint.highlight.filter(p => !taken.has(p.r * 100 + p.c)));
    view.preview(hint.actions); // 棋盘上直接显示将要进行的操作（幽灵标记）
    view.showFins(hint.fins); // 带鳍类技巧的"漏出格"红圈标出，与文案互认
    view.showLineBadges(hintLineRefs(hint)); // 文案点名的行/列号在棋盘边点亮
    renderHintText(
        $('#hint-text'),
        t(`play.hint.${hint.messageKey}`, HINT_FALLBACK[hint.messageKey] || ''),
        hint.meta,
    );
    $('#hint-bubble').hidden = false;
    $('#hint-apply').hidden = true;

    // 给玩家反应时间后再浮现「应用」——想自己动手的玩家不会被按钮打断思路
    const timer = setTimeout(() => { $('#hint-apply').hidden = false; }, HINT_APPLY_DELAY_MS);
    activeHint = { hint, timer };
}

/** 点击「应用」：把该步结论逐格落到棋盘。 */
async function onHintApply() {
    if (!activeHint) return;
    const { hint } = activeHint;
    dismissHint();

    applyingHint = true;
    input.enabled = false;
    for (const a of hint.actions) {
        session.applyHintAction(a);
        if (session.isOver) break;
        if (hint.actions.length > 1) await new Promise(res => setTimeout(res, HINT_STEP_MS));
    }
    applyingHint = false;
    if (!session.isOver) input.enabled = true;
}

/** 工具栏与 HUD 开关接线（只做一次）。 */
function wireChrome() {
    $('#btn-hint').addEventListener('click', onHintClick);
    $('#hint-apply').addEventListener('click', onHintApply);

    const themeBtn = $('#btn-theme');
    const syncTheme = () => {
        themeBtn.textContent = themeMode === 'dark' ? ICON_NIGHT : ICON_DAY;
        themeBtn.setAttribute('aria-pressed', String(themeMode === 'dark'));
    };
    syncTheme();
    themeBtn.addEventListener('click', () => {
        themeMode = themeMode === 'dark' ? 'light' : 'dark';
        applyTheme(themeMode);
        settings = saveSettings({ theme: themeMode });
        syncTheme();
    });

    const soundBtn = $('#btn-sound');
    const syncSound = () => {
        soundBtn.textContent = settings.sound ? ICON_SOUND_ON : ICON_SOUND_OFF;
        soundBtn.setAttribute('aria-pressed', String(settings.sound));
    };
    syncSound();
    soundBtn.addEventListener('click', () => {
        settings = saveSettings({ sound: !settings.sound });
        sfx.setEnabled(settings.sound);
        // 开启时立刻出个声，让玩家确认真的开了
        if (settings.sound) { sfx.unlock(); sfx.play('cross'); }
        syncSound();
    });

    $('#btn-lang').addEventListener('click', () => {
        showLanguage({
            current: lang(),
            names: LANG_NAMES,
            onPick: async code => {
                closePopup();
                await setLang(code);
                refreshText();
            },
        });
    });

    // 没手动选过主题时，跟随系统变化
    window.matchMedia?.('(prefers-color-scheme: dark)')?.addEventListener?.('change', () => {
        if (settings.theme) return;
        themeMode = systemMode();
        applyTheme(themeMode);
        syncTheme();
    });
}

/** 今天第一道还没解开的题；全解完返回 -1。 */
function firstUnsolvedSlot() {
    const { solved } = loadProgress(today);
    for (let i = 0; i < slots.length; i++) {
        if (!solved.includes(i)) return i;
    }
    return -1;
}

/** 今天已解开几道。 */
function solvedCount() {
    return loadProgress(today).solved.length;
}

/**
 * 开今天的第 n 道题：现取题面，再起一次遥测尝试（开题事件 + 计量器）。
 * 题面到这一刻才向服务端要 —— 没轮到的题不进浏览器。
 * @param {number} n
 * @param {{slot: number, size: string, sbn: string} | null} [prefetched]
 *   fetchDaily 顺带带回来的题面，slot 对得上就直接用，省一次 /api/puzzle 往返
 */
async function startSlot(n, prefetched) {
    slot = n;
    renderLevelChip();
    tracker?.abandon();
    const wantSlot = slots[n];
    const p = (prefetched && prefetched.slot === wantSlot) ? prefetched : await fetchPuzzle(wantSlot);
    sendStart(planDate, p.slot);
    tracker = new AttemptTracker(planDate, p.slot);
    startLevel(buildLevel(p.sbn, n + 1));
}

/**
 * HUD 左端的关卡胶囊：当前是今天的第几关。
 * 题单没到手时 slots 为空，只显示关号本身，别写出「1/0」这种数。
 */
function renderLevelChip() {
    const el = $('#level-text');
    if (!el) return;
    const n = slot + 1;
    el.textContent = slots.length ? `${n}/${slots.length}` : String(n);
    el.parentElement.setAttribute('aria-label', t('play.level', 'Level {n}').replace('{n}', String(n)));
}

/** 题面没取到（断网/服务异常）：给个能重来的出口，别把玩家晾在空棋盘上。 */
function showLoadError(e) {
    if (input) input.enabled = false;
    showError(
        e instanceof Error ? e.message : String(e),
        () => { closePopup(); location.reload(); },
    );
}

/** 当日题单未同步：占位页，不做本地兜底选题（plan v2 §1.2）。 */
function showPreparing() {
    if (input) input.enabled = false;
    showError(
        t('play.daily.preparing', "Today's puzzles are on their way — check back soon!"),
        () => { closePopup(); location.reload(); },
    );
}

/** 今天的题做完了。 */
/**
 * 今天的题做完了。
 * @param {{name?: string, email?: string}} [draft] 留资表单里还没提交的内容（从分享返回时带回来）
 */
function showAllDone(draft) {
    if (input) input.enabled = false;
    showDailyDone({
        total: slots.length || PUZZLES_PER_DAY,
        lead: loadLead(),
        draft,
        onSubmit: submitLead,
        // 分享关掉后要回到这个弹窗：它是玩家留昵称邮箱的唯一入口，
        // 直接 closePopup 会把表单丢掉，玩家再也填不了
        onShare: d => showShare(siteShareUrl(), () => showAllDone(d)),
    });
}

// 供调试面板直接唤起结束界面
window.__sdShowAllDone = showAllDone;

// ── 调试钩子（只被 ?debug=1 的面板调用） ──
//
// 这两个不含任何"专用通路"：调用的就是玩家双击走的 applyGesture，
// 胜负判定、扣命、进度写入、遥测上报全部由 session 自己按真实逻辑发出。
// 面板只是替你把鼠标快速点了一遍，所以测到的就是真行为。

/** 按唯一解逐格落星，最后一颗落下时 session 自己判通关。 */
window.__sdWin = () => {
    if (!session || !level || session.isOver) return;
    for (let r = 0; r < level.dim && !session.isOver; r++) {
        session.applyGesture(r, level.solution[r], 'double');
    }
};

/** 连点非解格子落红叉，扣满三条命后 session 自己判失败。 */
window.__sdLose = () => {
    if (!session || !level || session.isOver) return;
    for (let r = 0; r < level.dim && !session.isOver; r++) {
        for (let c = 0; c < level.dim && !session.isOver; c++) {
            if (c === level.solution[r]) continue; // 落在解上会得到星，扣不到命
            session.applyGesture(r, c, 'double');
        }
    }
};

/**
 * 开一关。
 * @param {import('./levels.js').Level} lv
 */
function startLevel(lv) {
    dismissHint();
    level = lv;
    session = new GameSession({
        dim: lv.dim,
        regionGrid: lv.regionGrid,
        stars: lv.stars,
        solution: lv.solution,
    });

    view.mount(session);
    input.attach(session);
    hud.mount(session);

    session.on('cellChanged', ({ to, from }) => {
        // 玩家自己落子 → 盘面已变，展示中的提示作废（代做期间除外）
        if (!applyingHint) dismissHint();

        if (to === 1) sfx.play('star');            // STAR
        else if (to === 3) {                       // RED_CROSS = 放置错误
            sfx.play('wrong');
            tracker?.wrong();
        }
        else if (to === 2) sfx.play('cross');      // WHITE_CROSS
        else if (from === 2) sfx.play('uncross');
    });
    session.on('ruleViolated', flashRules);
    session.on('completed', onCompleted);
    session.on('failed', onFailed);

    // 入场动画期间屏蔽输入，避免动画没播完就落子
    input.enabled = false;
    sfx.play('boardEnter', 0.7);
    view.playEnter().then(() => { input.enabled = true; });
}

/**
 * 违规的规则条闪红两次再回灰。
 * @param {Array<'rowcol'|'region'|'adjacent'>} keys
 */
function flashRules(keys) {
    for (const key of keys) {
        const el = document.querySelector(`.sd-rule[data-rule="${key}"]`);
        if (!el) continue;
        // 摘掉再强制回流，连续违反同一条时才会重播
        el.classList.remove('sd-rule--violated');
        void el.offsetWidth;
        el.classList.add('sd-rule--violated');
        el.addEventListener('animationend', function done() {
            el.classList.remove('sd-rule--violated');
            el.removeEventListener('animationend', done);
        });
    }
}

/**
 * 分享出去的永远是玩法页网址本身，绝不带 sbn ——
 * sbn 可直接解出题面与解，不能随链接散出去。
 */
function siteShareUrl() {
    // canonical 是线上正式域名；本地调试时没有就退回当前地址（去掉 query）
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    return canonical || `${location.origin}${location.pathname}`;
}

function onCompleted() {
    input.enabled = false;

    tracker?.finish({ solved: true });
    tracker = null;

    markSolved(today, slot);
    const done = solvedCount();
    const hasNext = done < slots.length;

    setTimeout(() => {
        // 今天的最后一道做完 → 直接给「明天再来」，不再走普通成功弹窗
        if (!hasNext) { showAllDone(); return; }

        showSuccess({
            level: slot + 1,
            total: slots.length,
            done,
            perfect: session.mistakes === 0,
            hasNext,
            onNext: () => {
                closePopup();
                const next = firstUnsolvedSlot();
                if (next === -1) { showAllDone(); return; }
                // 下一题的题面要现取，可能失败 —— 给玩家一个能重试的出口
                startSlot(next).catch(showLoadError);
            },
            onHome: () => { location.href = '/'; },
        });
    }, 420); // 让最后一颗星的弹出动画播完
}

function onFailed() {
    input.enabled = false;

    // 三错失败照常提交 attempt（solved=0，wrong_count 已计满）
    tracker?.finish({ solved: false });
    tracker = null;

    setTimeout(() => {
        showFailure({
            onRetry: () => {
                closePopup();
                dismissHint();
                session.reset();
                // 重玩 = 新一次尝试（attempt_no+1），重新起计量器。
                // 题面已经在手上，不用再取一次
                tracker = new AttemptTracker(planDate, slots[slot]);
                input.enabled = false;
                view.playEnter().then(() => { input.enabled = true; });
            },
            onHome: () => { location.href = '/'; },
        });
    }, 520); // 等红叉与碎心动画
}

/** 语言变化后重刷所有动态文案（静态的由 applyDom 处理）。 */
function refreshText() {
    applyDom();
    $('#date-text').textContent = formatDate(document.documentElement.lang || 'en');
    renderLevelChip();
}

boot();
